import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Loader2, Megaphone, Trash2, CalendarIcon, Send, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BANNER_TYPES = [
  { value: 'info',     label: 'Info (blue)' },
  { value: 'success',  label: 'Success (green)' },
  { value: 'warning',  label: 'Warning (yellow)' },
  { value: 'critical', label: 'Critical (pink/red)' },
];

export function BannerManager({ activeBanner, banners, onChange }) {
  const [bannerForm, setBannerForm] = useState({ message: '', type: 'info', expires_at: null });
  const [savingBanner, setSavingBanner] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [blastSubject, setBlastSubject] = useState('NCWI announcement');

  const handleCreateBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.message.trim()) {
      toast.error('Message is required');
      return;
    }
    setSavingBanner(true);
    try {
      const expiresIso = bannerForm.expires_at
        ? new Date(bannerForm.expires_at.getFullYear(), bannerForm.expires_at.getMonth(), bannerForm.expires_at.getDate(), 23, 59, 59).toISOString()
        : null;
      const payload = {
        message: bannerForm.message.trim(),
        type: bannerForm.type,
        expires_at: expiresIso,
      };
      await axios.post(`${API_URL}/api/admin/banners`, payload, { withCredentials: true });
      toast.success('Banner published');
      setBannerForm({ message: '', type: 'info', expires_at: null });
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish banner');
    } finally {
      setSavingBanner(false);
    }
  };

  const handleEmailBlast = async () => {
    if (!bannerForm.message.trim()) {
      toast.error('Message is required to send email blast');
      return;
    }
    if (!window.confirm(`Send this message as an email to ALL members?\n\nSubject: ${blastSubject}\n\n${bannerForm.message}`)) return;
    setBlasting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-blast`, {
        subject: blastSubject,
        message: bannerForm.message.trim(),
      }, { withCredentials: true });
      const { sent, failed, recipients } = res.data;
      toast.success(`Email blast sent — ${sent}/${recipients} delivered${failed ? ` (${failed} failed)` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email blast failed');
    } finally {
      setBlasting(false);
    }
  };

  const handleDeactivate = async (banner_id) => {
    try {
      await axios.put(`${API_URL}/api/admin/banners/${banner_id}/deactivate`, {}, { withCredentials: true });
      toast.success('Banner deactivated');
      await onChange();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  const handleDelete = async (banner_id) => {
    if (!window.confirm('Delete this banner permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/banners/${banner_id}`, { withCredentials: true });
      toast.success('Banner deleted');
      await onChange();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-bold text-black font-sans flex items-center gap-2">
          <Megaphone className="w-5 h-5" />
          Alert Banner
        </CardTitle>
        <p className="text-sm text-black/60 font-sans">Only one banner can be active at a time. Publishing a new one deactivates the previous.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {activeBanner ? (
          <div className="rounded-lg border-2 border-black bg-[#fef9e7] p-4" data-testid="active-banner-display">
            <p className="text-xs font-sans font-semibold text-black/60 mb-1">CURRENTLY ACTIVE</p>
            <p className="font-sans font-semibold text-black mb-2">{activeBanner.message}</p>
            <div className="flex flex-wrap items-center gap-2 text-xs font-sans text-black/70">
              <span className="rounded border-2 border-black px-2 py-0.5">{activeBanner.type}</span>
              {activeBanner.expires_at && (
                <span>expires: {new Date(activeBanner.expires_at).toLocaleDateString()}</span>
              )}
              <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={() => handleDeactivate(activeBanner.banner_id)} data-testid="deactivate-banner-btn">
                Deactivate
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-black/50 font-sans italic" data-testid="no-active-banner">No active banner.</p>
        )}

        <form onSubmit={handleCreateBanner} className="space-y-4">
          <h3 className="font-bold text-black font-sans">Publish a new banner</h3>
          <div>
            <Label className="font-sans font-semibold">Message</Label>
            <Textarea value={bannerForm.message} rows={2}
              onChange={(e) => setBannerForm({...bannerForm, message: e.target.value})}
              placeholder="e.g. Join our workshop this Friday at 6pm!"
              data-testid="banner-message-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-sans font-semibold">Type</Label>
              <Select value={bannerForm.type} onValueChange={(v) => setBannerForm({...bannerForm, type: v})}>
                <SelectTrigger data-testid="banner-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BANNER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-sans font-semibold">Expiry date (optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start font-sans font-normal h-10"
                    data-testid="banner-expiry-trigger"
                  >
                    <CalendarIcon className="w-4 h-4" />
                    {bannerForm.expires_at
                      ? bannerForm.expires_at.toLocaleDateString()
                      : <span className="text-black/40">Pick a date</span>}
                    {bannerForm.expires_at && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setBannerForm({...bannerForm, expires_at: null}); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setBannerForm({...bannerForm, expires_at: null}); } }}
                        className="ml-auto text-black/60 hover:text-black cursor-pointer"
                        data-testid="banner-expiry-clear"
                        aria-label="Clear date"
                      >
                        <X className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#fef9e7] border-[3px] border-black rounded-lg shadow-[6px_6px_0px_#000]" align="start">
                  <Calendar
                    mode="single"
                    selected={bannerForm.expires_at}
                    onSelect={(d) => setBannerForm({...bannerForm, expires_at: d})}
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
            <Label className="font-sans font-semibold">Email subject (used only when blasting as email)</Label>
            <Input
              value={blastSubject}
              onChange={(e) => setBlastSubject(e.target.value)}
              data-testid="blast-subject-input"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={savingBanner || blasting} data-testid="publish-banner-btn">
              {savingBanner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              {savingBanner ? 'Publishing…' : 'Publish Banner'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleEmailBlast}
              disabled={savingBanner || blasting}
              data-testid="email-blast-btn"
            >
              {blasting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {blasting ? 'Sending…' : 'Send as Email'}
            </Button>
          </div>
        </form>

        {banners.length > 0 && (
          <div>
            <h3 className="font-bold text-black font-sans mb-2">Banner History</h3>
            <div className="space-y-2">
              {banners.map(b => (
                <div key={b.banner_id} className="flex items-start gap-3 rounded-md border-2 border-black p-3 bg-white" data-testid={`banner-history-${b.banner_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm text-black truncate">{b.message}</p>
                    <p className="text-xs text-black/60 font-sans mt-1">
                      {b.type} · {new Date(b.created_at).toLocaleDateString()}
                      {b.is_active && <span className="ml-2 bg-[#ffd703] border border-black px-1">ACTIVE</span>}
                    </p>
                  </div>
                  <button type="button" onClick={() => handleDelete(b.banner_id)}
                    className="rounded-md border-2 border-black bg-red-500 text-white p-1.5 hover:translate-y-[1px]"
                    aria-label="Delete banner"
                    data-testid={`delete-banner-${b.banner_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
